/**
 * Sign-in links carry a credential in the URL: `?token=` on the survey,
 * consent, coach-matches, booking, change-plan and verify-coverage links, and
 * a path segment on `/subscribe/<token>` and `/a/<token>`. Every third party
 * on the page — GTM's Google tags, the Meta/TikTok/Bing pixels, a Referer
 * header — reads the page URL, so the token must be gone from the address bar
 * before any of them runs.
 *
 * The same links carry what identifies the client beside the token: the
 * internal client id on consent and sign-up links, and on the post-call
 * survey the answer the client tapped (`responseId`) and the call it rates
 * (`callId`). Those leave with the token. `questionId` stays: it names the
 * question, not the answer, and survey pages route on it. Only a link that
 * carried a credential is cleaned this way, so an admin's `?clientId=` view
 * of the same page keeps working across a refresh.
 *
 * The server still sees the original request: getInitialProps and
 * getServerSideProps exchange the token as before, and SSR pages keep it in
 * router.query (it rides in __NEXT_DATA__), so page logic is unchanged. Only
 * what the browser shows, and therefore what scripts read, is cleaned.
 *
 * `/video/<token>` is a credential too, but it stays in the address bar: the
 * page reads it on every load, so stripping it would break a reload. That
 * page loads no third-party tags and sends no Referer instead
 * (utilities/untracked-pages.ts). Keep this list in step with
 * utilities/secret-urls.ts.
 *
 * Both functions below are inlined into a <head> script by pages/_document.js
 * via Function#toString, so each must stay self-contained: no imports, no
 * references outside its own body, and plain ES5 (no spread, for-of or
 * destructuring, which a compiler could turn into helper calls).
 */

/**
 * Returns `url` (relative or absolute) with credentials (and, on a sign-in
 * link, the client's ids) removed, as a root-relative path + query + hash, or
 * null when there was nothing to remove or the URL is another origin's.
 * Credential params named in `keepParams` stay in this URL's own query; a URL
 * nested in a parameter loses them all.
 */
export function cleanCredentialUrl(url, baseHref, keepParams) {
  if (url === null || url === undefined || url === '') return null
  var parsed
  var base
  try {
    base = new URL(baseHref)
    parsed = new URL(String(url), base)
  } catch (e) {
    return null
  }
  if (parsed.origin !== base.origin) return null

  var changed = false
  // Whether this is a sign-in link at all, kept credential or not.
  var carriedCredential = false
  var CREDENTIAL_PARAMS = ['token', 'paymentToken']
  for (var i = 0; i < CREDENTIAL_PARAMS.length; i++) {
    if (!parsed.searchParams.has(CREDENTIAL_PARAMS[i])) continue
    carriedCredential = true
    if (keepParams && keepParams.indexOf(CREDENTIAL_PARAMS[i]) !== -1) continue
    parsed.searchParams.delete(CREDENTIAL_PARAMS[i])
    changed = true
  }

  // A URL carried in a parameter (verify-coverage's `returnTo` holds the
  // booking link, token and all) is cleaned the same way, whatever its origin:
  // a credential anywhere in our address bar is readable by trackers. Which
  // values to look at is decided by what they contain (a credential param or
  // a path-token segment), not by how they start, because the URL parser
  // accepts more spellings than any prefix list (whitespace, `\` for `/`,
  // protocol-relative). The value is then read with that same parser.
  var nested = []
  parsed.searchParams.forEach(function (value, key) {
    if (
      !/(token|paymentToken)=/.test(value) &&
      !/[\/\\](subscribe|a)[\/\\][^\/\\?#]/.test(value)
    ) {
      return
    }
    var nestedUrl
    try {
      nestedUrl = new URL(value, base.href)
    } catch (e) {
      return
    }
    // An opaque scheme (`mailto:`, `javascript:`) has no origin to rebuild.
    if (!/^https?:$/.test(nestedUrl.protocol)) return
    // Nested credentials always go: `keepParams` is for what a reader of
    // this URL's own query needs, and a nested value arrives percent-encoded.
    var cleanedValue = cleanCredentialUrl(nestedUrl.href, nestedUrl.href)
    if (cleanedValue === null) return
    // Keep a root-relative value root-relative; anything else comes back
    // absolute.
    var trimmed = value.replace(/^\s+/, '')
    var isRootRelative =
      /^[\/\\]/.test(trimmed) &&
      !/^[\/\\]{2}/.test(trimmed) &&
      nestedUrl.origin === base.origin
    nested.push([
      key,
      isRootRelative ? cleanedValue : nestedUrl.origin + cleanedValue,
    ])
  })
  for (var n = 0; n < nested.length; n++) {
    parsed.searchParams.set(nested[n][0], nested[n][1])
    changed = true
  }

  // Path tokens. Each maps to a page that renders from the session cookie the
  // server just set (/subscribe), or that has nothing secret to show without
  // the code (/auth-tokens). /subscribe/update-payment-method is a real page.
  var PATH_CREDENTIALS = [
    [/^\/subscribe\/(?!update-payment-method\/?$)[^/]+\/?$/, '/subscribe'],
    [/^\/a\/[^/]+\/?$/, '/auth-tokens'],
  ]
  for (var j = 0; j < PATH_CREDENTIALS.length; j++) {
    if (PATH_CREDENTIALS[j][0].test(parsed.pathname)) {
      parsed.pathname = PATH_CREDENTIALS[j][1]
      carriedCredential = true
      changed = true
    }
  }

  // What identifies the client on a sign-in link's landing page. Each page
  // reads these on the server (they ride in __NEXT_DATA__ as router.query),
  // and falls back to the session the link just set when a refresh arrives
  // without them.
  if (carriedCredential || changed) {
    var CLIENT_LINK_IDS = [
      [/^\/consent\/?$/, ['clientId']],
      [/^\/client-sign-up(\/[^/]+)?\/?$/, ['clientId']],
      [/^\/survey(\/[^/]+)?\/?$/, ['responseId', 'callId']],
    ]
    for (var p = 0; p < CLIENT_LINK_IDS.length; p++) {
      if (!CLIENT_LINK_IDS[p][0].test(parsed.pathname)) continue
      for (var q = 0; q < CLIENT_LINK_IDS[p][1].length; q++) {
        if (!parsed.searchParams.has(CLIENT_LINK_IDS[p][1][q])) continue
        parsed.searchParams.delete(CLIENT_LINK_IDS[p][1][q])
        changed = true
      }
    }
  }

  if (!changed) return null
  return parsed.pathname + parsed.search + parsed.hash
}

/**
 * Cleans the address bar now, and keeps it clean: a client-side navigation
 * that carries a token (a page forwarding router.query.token, say) goes
 * through history.pushState/replaceState, which are wrapped here before any
 * tracker loads. Next.js's own history state (`__N`) is cleaned alongside, so
 * back/forward navigation never restores a token either.
 *
 * Inside the app's sign-up WebView (react-native-webview defines
 * `window.ReactNativeWebView`), paymentToken stays in the URL for app builds
 * that read it there. Those builds wrap pushState themselves, read
 * `paymentToken=` from window.location.href after each push, and sign the
 * guest in with it before opening the native paywall
 * (mobile/src/components/client-sign-up-survey), so stripping it leaves a
 * paying client signed out. Builds that take the token as a bridge message
 * instead announce `window.__kickoffAppCapabilities.paymentTokenMessage`
 * before any page script runs (utilities/app-payment-token); there it goes
 * like everywhere else. The check runs on every call, not once, so the guard
 * never depends on whether the bridge or the capability exists before this
 * script does: until the capability shows up, the token is kept.
 */
export function installCredentialUrlGuard(win, clean) {
  var history = win.history
  if (!history || typeof history.replaceState !== 'function') return

  var cleanHere = function (url) {
    var capabilities = win.__kickoffAppCapabilities
    var appReadsUrlToken =
      win.ReactNativeWebView &&
      !(capabilities && capabilities.paymentTokenMessage)
    return clean(
      url,
      win.location.href,
      appReadsUrlToken ? ['paymentToken'] : null
    )
  }

  var cleanState = function (state) {
    if (!state || typeof state !== 'object' || !state.__N) return state
    var cleanedAs = cleanHere(state.as)
    var cleanedUrl = cleanHere(state.url)
    if (cleanedAs === null && cleanedUrl === null) return state
    var copy = {}
    for (var key in state) {
      if (Object.prototype.hasOwnProperty.call(state, key)) {
        copy[key] = state[key]
      }
    }
    if (cleanedAs !== null) copy.as = cleanedAs
    // A path token changes the route itself, so the route href must follow
    // the cleaned `as` or Next.js would reject the pair on popstate.
    if (
      cleanedAs !== null &&
      String(state.as).split('?')[0] !== cleanedAs.split('?')[0]
    ) {
      copy.url = cleanedAs
    } else if (cleanedUrl !== null) {
      copy.url = cleanedUrl
    }
    return copy
  }

  var wrap = function (method) {
    var original = history[method]
    history[method] = function (state, title, url) {
      var cleanedUrl = cleanHere(url)
      return original.call(
        this,
        cleanState(state),
        title,
        cleanedUrl === null ? url : cleanedUrl
      )
    }
  }
  wrap('pushState')
  wrap('replaceState')

  var cleanedLocation = cleanHere(win.location.href)
  if (cleanedLocation !== null) {
    history.replaceState(history.state, '', cleanedLocation)
  }
}

/** URL with credentials removed, for code that reports page URLs. */
export const withoutCredentials = url => {
  if (typeof window === 'undefined') return url
  const cleaned = cleanCredentialUrl(url, window.location.href)
  return cleaned === null ? url : cleaned
}

/**
 * The inline <head> script for pages/_document.js. A failure here must never
 * take the page down with it, so it is caught and the page renders as before.
 */
export const CREDENTIAL_URL_GUARD_SCRIPT = `try { (${installCredentialUrlGuard.toString()})(window, ${cleanCredentialUrl.toString()}); } catch (e) {}`
